import { useLearningUiTranslation } from '@features/learning/shared/use-learning-ui-translation'
import { CheckCircle2, CirclePlus, Eye } from 'lucide-react'
import type { FlashcardCard as FlashcardCardModel } from '@services/firebase/flashcards'
import { InlineAudioPlayer } from '@shared/media/audio/InlineAudioPlayer'
import {
  LmsButton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/ui'
import './flashcard-collection.css'

export function FlashcardCard({
  audio,
  card,
  isSelected,
  onPreview,
  onToggleSelected,
  translation,
}: {
  readonly audio?: string | null
  readonly card: FlashcardCardModel
  readonly isSelected: boolean
  readonly onPreview: () => void
  readonly onToggleSelected: () => void
  readonly translation?: string
}) {
  const ui = useLearningUiTranslation()
  return (
    <article className="flashcardCollectionCard">
      {card.featuredImage ? (
        <img
          alt={card.title ?? card.id}
          className="flashcardCollectionCardImage"
          src={card.featuredImage}
        />
      ) : null}
      <div className="flashcardCollectionCardBody">
        <div className="flashcardCollectionCardContent">
          <div className="flashcardCollectionCardHeader">
            <div className="flashcardCollectionCardCopy">
              <h6 className="flashcardCollectionCardTitle">
                {card.title ?? card.id}
              </h6>
              {translation ? (
                <p className="flashcardCollectionCardDescription">
                  {translation}
                </p>
              ) : null}
            </div>
            {isSelected ? (
              <span className="flashcardCollectionSelectedBadge">{ui("Selected")}</span>
            ) : null}
          </div>

          <TooltipProvider delayDuration={250}>
            <div className="flashcardCollectionCardActions">
              <div className="flashcardCollectionCardMediaActions">
                <InlineAudioPlayer audio={audio} labels={{
                  play: ui('Play audio'),
                  stop: ui('Stop audio'),
                  unavailable: ui('Audio playback is unavailable for this clip in the current browser.'),
                }} />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <LmsButton
                        aria-label={ui("Preview in slideshow")}
                        disabled={!card.featuredImage}
                        onClick={onPreview}
                        size="icon"
                        variant="outline"
                      >
                        <Eye aria-hidden="true" className="size-4" />
                      </LmsButton>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{ui("Preview in slideshow")}</TooltipContent>
                </Tooltip>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <LmsButton
                    aria-label={isSelected ? ui("Remove from collection") : ui("Add to collection")}
                    aria-pressed={isSelected}
                    onClick={onToggleSelected}
                    size="icon"
                    variant={isSelected ? 'secondary' : 'outline'}
                  >
                    {isSelected ? (
                      <CheckCircle2 aria-hidden="true" className="size-4" />
                    ) : (
                      <CirclePlus aria-hidden="true" className="size-4" />
                    )}
                  </LmsButton>
                </TooltipTrigger>
                <TooltipContent>
                  {isSelected ? ui("Remove from collection") : ui("Add to collection")}
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>
      </div>
    </article>
  )
}
